import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, Twitter, Facebook, ArrowUpRight, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT, SERVICES, FOUNDER } from "@/data/services";

const SOCIALS = [
  { icon: Instagram, label: "Instagram", testId: "social-instagram" },
  { icon: Facebook, label: "Facebook", testId: "social-facebook" },
  { icon: Twitter, label: "X", testId: "social-twitter" },
  { icon: Youtube, label: "YouTube", testId: "social-youtube" },
  { icon: Linkedin, label: "LinkedIn", testId: "social-linkedin" },
];

const PAGES = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/packages", label: "Packages" },
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10">
      <div className="tricolor-line h-[3px] w-full" />
      <div className="mx-auto max-w-[1440px] px-6 pb-10 pt-20 lg:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo stacked testId="footer-logo" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-zinc-400">
              Political campaign management and creative agency from Indore, Madhya Pradesh. Election
              strategy, digital war rooms, ground outreach, films and brand marketing — across India.
            </p>
            <p className="mt-4 font-hindi text-lg text-saffron">आगे बढ़ो। हमेशा।</p>
            <div className="mt-8 flex gap-3">
              {SOCIALS.map(({ icon: Icon, label, testId }) => (
                <a
                  key={label}
                  href="#"
                  data-testid={testId}
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-400 transition-colors duration-300 hover:border-saffron hover:text-saffron"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Sitemap</p>
            <ul className="mt-6 space-y-3">
              {PAGES.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="group inline-flex items-center gap-1 text-sm text-zinc-300 transition-colors duration-300 hover:text-brand"
                  >
                    {l.label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Services</p>
            <ul className="mt-6 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to="/services" data-testid={`footer-service-${s.slug}`} className="text-sm text-zinc-300 transition-colors duration-300 hover:text-brand">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Contact</p>
            <ul className="mt-6 space-y-3 text-sm text-zinc-300">
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone" className="transition-colors duration-300 hover:text-saffron">
                  {CONTACT.phone}
                </a>
                <p className="text-xs text-zinc-500">{FOUNDER.name}, {FOUNDER.role}</p>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="transition-colors duration-300 hover:text-brand">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-zinc-400">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
                <span>
                  {CONTACT.address[0]}
                  <br />
                  {CONTACT.address[1]}
                </span>
              </li>
              <li>
                <Link to="/admin" data-testid="footer-admin-link" className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 transition-colors duration-300 hover:text-brand">
                  Admin
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 select-none overflow-hidden" aria-hidden="true">
          <p className="text-stroke whitespace-nowrap font-display text-[18vw] font-bold uppercase leading-[0.85] tracking-tight lg:text-[13vw]">
            Onward
          </p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">
            © 2026 Onward Media, Indore. All rights reserved.
          </p>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">Made in Madhya Pradesh, India</p>
        </div>
      </div>
    </footer>
  );
}
